import type {ReactElement} from 'react';
import type {ComponentStreamEvent} from '@Components/ai-chat-window/ai-chat-window.types';
import {ChatMessagePartType} from '@Components/ai-chat-window/ai-chat-window.types';
import type {ChatMessageComponentPart, RegisteredStreamComponent, StreamComponentContext, StreamComponentRegistration} from '@Components/ai-chat-window/lib/stream-component-registry.types';
import type {StreamComponentType} from '@Components/ai-chat-window/lib/stream-component-registry.types';
import {createRegistry} from '@Components/ai-chat-window/lib/create-registry';
import {planTableRegistration} from '@Components/ai-chat-window/components/plan-table/plan-table.registration';
import {buttonGroupRegistration} from '@Components/ai-chat-window/components/button-group/button-group.registration';
import {entityDraftCardRegistration} from '@Components/ai-chat-window/components/entity-draft-card/entity-draft-card.registration';
import {entityDraftSkeletonRegistration} from '@Components/ai-chat-window/components/entity-draft-skeleton/entity-draft-skeleton.registration';
import {entityCardRegistration} from '@Components/ai-chat-window/components/entity-card/entity-card.registration';
import {buttonGroupSkeletonRegistration} from '@Components/ai-chat-window/components/button-group-skeleton/button-group-skeleton.registration';
import {entityCardSkeletonRegistration} from '@Components/ai-chat-window/components/entity-card-skeleton/entity-card-skeleton.registration';

/**
 * Erase a typed registration's TProps for the registry: parseProps widens to unknown for
 * free, and render is wrapped to re-assert TProps at the boundary parseProps already
 * validated. Type-checked at registration time, so a mismatched parse/render pairing fails
 * to compile here rather than being silenced by an `as unknown as` at the collection site.
 */
function defineStreamComponent<TComponentType extends StreamComponentType, TProps>(
  registration: StreamComponentRegistration<TComponentType, TProps>
): RegisteredStreamComponent {
  return {
    componentType: registration.componentType,
    parseProps: registration.parseProps,
    render: (props, context): ReactElement => registration.render(props as TProps, context),
  };
}

const REGISTRY = createRegistry(
  [
    defineStreamComponent(planTableRegistration),
    defineStreamComponent(buttonGroupRegistration),
    defineStreamComponent(entityDraftCardRegistration),
    defineStreamComponent(entityDraftSkeletonRegistration),
    defineStreamComponent(entityCardRegistration),
    defineStreamComponent(buttonGroupSkeletonRegistration),
    defineStreamComponent(entityCardSkeletonRegistration),
  ],
  (registration) => registration.componentType
);

export const createComponentPart = (event: Pick<ComponentStreamEvent, 'componentType' | 'props'>): ChatMessageComponentPart | null => {
  const entry = REGISTRY[event.componentType];
  if (!entry) {
    return null;
  }

  const parsedProps = entry.parseProps(event.props);
  if (parsedProps === null) {
    console.error(`Dropped malformed ${event.componentType} stream component payload`);
    return null;
  }

  return {
    type: ChatMessagePartType.COMPONENT,
    componentType: event.componentType,
    props: parsedProps,
  } as ChatMessageComponentPart;
};

export const renderStreamComponent = (part: ChatMessageComponentPart, context: StreamComponentContext): ReactElement | null => {
  const entry = REGISTRY[part.componentType];
  if (!entry) {
    return null;
  }
  return entry.render(part.props, context);
};
